import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken, setOrgContext } from '../../api/client';
import '../../pages/OpsHome.css';
import rypilLogoWhite from '../../Images/rypilLogow.png';

const BASE_NAV_ITEMS = [
  { key: 'overview', label: 'Overview', route: '/ops' },
  { key: 'money_movement', label: 'Money Movement', route: '/ops/money-movement', permission: 'canViewMoneyMovement' },
  { key: 'performance', label: 'Performance', route: '/ops/performance' },
  { key: 'consumer_metrics', label: 'Consumer Metrics', route: '/ops/consumer-metrics' },
  { key: 'business_metrics', label: 'Business Metrics', route: '/ops/business-metrics' },
  { key: 'loop_health', label: 'Loop Health', route: '/ops/loop-health', permission: 'canViewCoverageIntelligence' },
  { key: 'markets', label: 'Markets', route: '/ops/markets' },
  { key: 'consumer_support', label: 'Consumer Support', route: '/ops/consumers/support' },
  { key: 'nfp_monitoring', label: 'NFP Monitoring', route: '/ops/nfps' },
  { key: 'business_monitoring', label: 'Business Monitoring', route: '/ops/businesses' },
  { key: 'tx_match_queue', label: 'Txn Match Queue', route: '/ops/transaction-match-queue' },
];

export default function OpsLayout({ title, subtitle, headerActions = null, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [canViewMoneyMovement, setCanViewMoneyMovement] = useState(true);
  const [canViewCoverageIntelligence, setCanViewCoverageIntelligence] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadOpsPermissions() {
      try {
        const data = await apiFetch('/ops/me');
        if (cancelled) return;
        setCanManageUsers(!!data?.internalOpsMembership?.permissions?.canManageInternalUsers);
        setCanViewMoneyMovement(data?.internalOpsMembership?.permissions?.canViewMoneyMovement !== false);
        setCanViewCoverageIntelligence(data?.internalOpsMembership?.permissions?.canViewCoverageIntelligence === true);
      } catch (_err) {
        if (!cancelled) {
          setCanManageUsers(false);
          setCanViewMoneyMovement(false);
          setCanViewCoverageIntelligence(false);
        }
      }
    }
    loadOpsPermissions();
    return () => {
      cancelled = true;
    };
  }, []);

  const navItems = useMemo(() => {
    const items = BASE_NAV_ITEMS.filter((item) => {
      if (!item.permission) return true;
      if (item.permission === 'canViewMoneyMovement') return canViewMoneyMovement;
      if (item.permission === 'canViewCoverageIntelligence') return canViewCoverageIntelligence;
      return true;
    });
    if (canManageUsers) {
      items.push({ key: 'users', label: 'Users', route: '/ops/users' });
    }
    return items;
  }, [canManageUsers, canViewCoverageIntelligence, canViewMoneyMovement]);

  const activeNavKey = useMemo(() => {
    const path = location?.pathname || '';
    if (path.startsWith('/ops/money-movement')) return 'money_movement';
    if (path.startsWith('/ops/performance')) return 'performance';
    if (path.startsWith('/ops/consumer-metrics')) return 'consumer_metrics';
    if (path.startsWith('/ops/business-metrics')) return 'business_metrics';
    if (path.startsWith('/ops/loop-health')) return 'loop_health';
    if (path.startsWith('/ops/markets')) return 'markets';
    if (path.startsWith('/ops/consumers/')) return 'consumer_support';
    if (path.startsWith('/ops/nfps')) return 'nfp_monitoring';
    if (path.startsWith('/ops/businesses')) return 'business_monitoring';
    if (path.startsWith('/ops/transaction-match-queue')) return 'tx_match_queue';
    if (path.startsWith('/ops/users')) return 'users';
    return 'overview';
  }, [location?.pathname]);

  function handleSignOut() {
    clearAuthToken();
    setOrgContext(null);
    navigate('/login', { replace: true });
  }

  return (
    <div className="ops-shell">
      <aside className={`ops-sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}>
        <button
          type="button"
          className="ops-toggle"
          onClick={() => setSidebarOpen((v) => !v)}
          aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {sidebarOpen ? '<<' : '>>'}
        </button>

        <div className="ops-logo">
          {sidebarOpen ? (
            <>
              <img className="ops-logo-image" src={rypilLogoWhite} alt="Rypil" />
              <div className="ops-logo-text">rypil Ops</div>
            </>
          ) : 'R'}
        </div>

        <nav className="ops-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`ops-nav-item ${activeNavKey === item.key ? 'active' : ''}`}
              onClick={() => navigate(item.route)}
            >
              {sidebarOpen ? item.label : item.label.slice(0, 1)}
            </button>
          ))}
          <button
            type="button"
            className="ops-nav-item ops-signout"
            onClick={handleSignOut}
          >
            {sidebarOpen ? 'Sign out' : 'X'}
          </button>
        </nav>
      </aside>

      <main className="ops-main">
        <header className="ops-header">
          <div>
            <h1>{title}</h1>
            {subtitle ? (
              typeof subtitle === 'string'
                ? <p>{subtitle}</p>
                : <div className="ops-header-subtitle">{subtitle}</div>
            ) : null}
          </div>
          {headerActions ? <div className="ops-header-actions">{headerActions}</div> : null}
        </header>
        {children}
      </main>
    </div>
  );
}
